import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Sparkles, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedTools from '../components/RelatedTools';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { safeJsonLd } from '../lib/sanitize';
import { useLang } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  { key: 'past', label_hi: 'अतीत', label_en: 'Past', color: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  { key: 'present', label_hi: 'वर्तमान', label_en: 'Present', color: 'from-gold/20 to-yellow-600/10', border: 'border-gold/30', text: 'text-gold' },
  { key: 'future', label_hi: 'भविष्य', label_en: 'Future', color: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/30', text: 'text-purple-400' },
];

const CARD_BACK_PATTERN = "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.15) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(139,92,246,0.15) 0%, transparent 50%), linear-gradient(135deg, #1a0a2e 0%, #0d0520 50%, #1a0a2e 100%)";

function TarotCard({ card, position, index, isRevealed, onReveal, lang }) {
  const pos = POSITIONS[index];
  
  return (
    <div className="flex flex-col items-center gap-3">
      <span className={`text-xs font-semibold tracking-widest uppercase ${pos.text}`}>
        {lang === 'hi' ? pos.label_hi : pos.label_en}
      </span>
      
      <div className="perspective-1000" style={{ perspective: '1000px' }}>
        <motion.div
          className="relative w-[110px] h-[170px] sm:w-[140px] sm:h-[210px] cursor-pointer"
          style={{ transformStyle: 'preserve-3d' }}
          animate={{ rotateY: isRevealed ? 180 : 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          onClick={() => !isRevealed && onReveal(index)}
          whileHover={!isRevealed ? { scale: 1.05, y: -5 } : {}}
          data-testid={`tarot-card-${index}`}
        >
          {/* Card Back */}
          <div 
            className="absolute inset-0 rounded-xl border-2 border-gold/30 flex items-center justify-center overflow-hidden"
            style={{ background: CARD_BACK_PATTERN, backfaceVisibility: 'hidden' }}
          >
            <div className="absolute inset-3 border border-gold/20 rounded-lg" />
            <div className="absolute inset-6 border border-gold/10 rounded-md" />
            <span className="text-4xl sm:text-5xl text-gold/30 font-heading">ॐ</span>
            {!isRevealed && (
              <motion.div className="absolute inset-0 bg-gold/5"
                animate={{ opacity: [0, 0.15, 0] }}
                transition={{ duration: 2, repeat: Infinity }} />
            )}
          </div>

          {/* Card Front */}
          <div 
            className={`absolute inset-0 rounded-xl border-2 ${pos.border} bg-gradient-to-b ${pos.color} backdrop-blur-sm flex flex-col items-center justify-center p-3 text-center`}
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <span className="text-3xl sm:text-4xl mb-2">{
              card?.name?.includes('Wands') ? '🪄' :
              card?.name?.includes('Cups') ? '🏆' :
              card?.name?.includes('Swords') ? '⚔️' :
              card?.name?.includes('Pentacles') ? '⭐' :
              card?.name?.includes('Fool') ? '🃏' :
              card?.name?.includes('Magician') ? '🎭' :
              card?.name?.includes('Priestess') ? '🌙' :
              card?.name?.includes('Empress') ? '👑' :
              card?.name?.includes('Emperor') ? '🏛️' :
              card?.name?.includes('Lovers') ? '💕' :
              card?.name?.includes('Chariot') ? '🏇' :
              card?.name?.includes('Strength') ? '🦁' :
              card?.name?.includes('Hermit') ? '🏔️' :
              card?.name?.includes('Wheel') ? '☸️' :
              card?.name?.includes('Justice') ? '⚖️' :
              card?.name?.includes('Hanged') ? '🔮' :
              card?.name?.includes('Death') ? '🦋' :
              card?.name?.includes('Temperance') ? '⏳' :
              card?.name?.includes('Devil') ? '🔥' :
              card?.name?.includes('Tower') ? '⚡' :
              card?.name?.includes('Star') ? '⭐' :
              card?.name?.includes('Moon') ? '🌙' :
              card?.name?.includes('Sun') ? '☀️' :
              card?.name?.includes('Judgement') ? '📯' :
              card?.name?.includes('World') ? '🌍' : '🔮'
            }</span>
            <p className={`text-[10px] sm:text-xs font-bold ${pos.text} leading-tight`}>{card?.name_hi || card?.name}</p>
            <p className="text-[9px] text-text-muted mt-1">{card?.name}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default function DailyTarotPage() {
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState([false, false, false]);
  const [allRevealed, setAllRevealed] = useState(false);
  const [started, setStarted] = useState(false);
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  const getSessionId = useCallback(() => {
    let sid = localStorage.getItem('tarot_session');
    if (!sid) { sid = 'tarot-' + Math.random().toString(36).substr(2, 9); localStorage.setItem('tarot_session', sid); }
    return sid;
  }, []);

  const startReading = async () => {
    setStarted(true);
    setLoading(true);
    setRevealed([false, false, false]);
    setAllRevealed(false);
    try {
      const res = await axios.post(`${API}/api/daily-tarot`, { session_id: getSessionId(), language: lang });
      setReading(res.data);
    } catch (err) {
      console.error('Tarot error:', err);
    } finally {
      setLoading(false);
    }
  };

  const revealCard = (index) => {
    const newRevealed = [...revealed];
    newRevealed[index] = true;
    setRevealed(newRevealed);
    if (newRevealed.every(r => r)) {
      setTimeout(() => setAllRevealed(true), 1000);
    }
  };

  const newReading = () => {
    localStorage.removeItem('tarot_session');
    setReading(null);
    setStarted(false);
    setRevealed([false, false, false]);
    setAllRevealed(false);
  };

  const today = new Date().toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Daily Tarot Card Reading | Free 3-Card Spread | टैरो"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'टैरो रीडिंग', path: '/daily-tarot' },
        ]}
        description="Free Daily Tarot Card reading — 3-card spread for love, career, and life guidance. AI-powered interpretation of past, present, and future cards by Vivek Mudgal Astrologer। फ्री टैरो रीडिंग हिंदी में।"
        path="/daily-tarot"
        keywords="daily tarot, tarot card reading, free tarot, 3 card spread, टैरो, tarot in hindi, love tarot, career tarot, Vivek Mudgal"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Daily Tarot Card Reading',
          description: 'Free daily 3-card tarot spread with AI-powered interpretation',
          provider: { '@type': 'Person', name: 'Vivek Mudgal' },
          areaServed: 'India',
        }}
      />
      <Header />
      <FloatingActions />

      {/* Hero */}
      <section className="pt-32 pb-8 cosmic-bg">
        <div className="container-custom text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-4">
            {lang === 'hi' ? 'दैनिक टैरो रीडिंग' : 'DAILY TAROT READING'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main mb-4">
            {lang === 'hi' ? 'आज का टैरो कार्ड' : "Today's Tarot Cards"}
          </h1>
          <p className="text-lg text-gold mb-2">{today}</p>
          <p className="text-text-muted max-w-xl mx-auto">
            {lang === 'hi'
              ? '3 कार्ड स्प्रेड — अतीत, वर्तमान और भविष्य जानें वैदिक उपाय सहित'
              : '3 Card Spread — Discover your Past, Present & Future with Vedic remedies'}
          </p>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>
      </section>

      {/* Tarot Section */}
      <section className="section-padding">
        <div className="container-custom max-w-3xl">
          
          {!started ? (
            /* Start Screen */
            <motion.div className="text-center py-12"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex justify-center gap-4 mb-8">
                {[0, 1, 2].map(i => (
                  <motion.div key={i} className="w-[80px] h-[120px] sm:w-[100px] sm:h-[150px] rounded-xl border-2 border-gold/20"
                    style={{ background: CARD_BACK_PATTERN }}
                    animate={{ y: [0, -8, 0], rotate: [i === 0 ? -5 : i === 2 ? 5 : 0, i === 0 ? -3 : i === 2 ? 3 : 0, i === 0 ? -5 : i === 2 ? 5 : 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}>
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-2xl sm:text-3xl text-gold/20">ॐ</span>
                    </div>
                  </motion.div>
                ))}
              </div>
              <p className="text-text-muted mb-6 text-lg">
                {lang === 'hi' ? 'मन शांत करें, गहरी सांस लें, और अपना प्रश्न मन में रखें' : 'Clear your mind, take a deep breath, and focus on your question'}
              </p>
              <button onClick={startReading} className="btn-gold text-lg px-10 py-4" data-testid="start-tarot-btn">
                <Sparkles className="w-5 h-5 inline mr-2" />
                {lang === 'hi' ? 'कार्ड निकालें' : 'Draw Cards'}
              </button>
            </motion.div>
          ) : loading ? (
            /* Loading */
            <div className="text-center py-16">
              <motion.div className="text-5xl mb-4"
                animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
                🔮
              </motion.div>
              <p className="text-gold animate-pulse text-lg">{lang === 'hi' ? 'ब्रह्मांड से जुड़ रहे हैं...' : 'Connecting with the cosmos...'}</p>
            </div>
          ) : reading?.cards?.length >= 3 ? (
            /* Cards */
            <>
              <div className="flex justify-center gap-4 sm:gap-8 mb-10" data-testid="tarot-cards-container">
                {reading.cards.map((card, i) => (
                  <TarotCard key={i} card={card} position={POSITIONS[i].key} index={i}
                    isRevealed={revealed[i]} onReveal={revealCard} lang={lang} />
                ))}
              </div>

              {!allRevealed && !revealed.every(r => r) && (
                <p className="text-center text-text-muted text-sm animate-pulse">
                  {lang === 'hi' ? 'कार्ड पर tap करें reveal करने के लिए' : 'Tap on cards to reveal them'}
                </p>
              )}

              {/* Reading */}
              <AnimatePresence>
                {allRevealed && (
                  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    
                    {/* Individual Card Meanings */}
                    <div className="space-y-4 mb-8" data-testid="card-meanings">
                      {reading.cards.map((card, i) => (
                        <div key={i} className={`glass-card p-5 border ${POSITIONS[i].border}`}>
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`text-xs font-bold tracking-wider uppercase ${POSITIONS[i].text}`}>
                              {lang === 'hi' ? card.position_hi : card.position}
                            </span>
                            <span className="text-text-main font-heading text-lg">{card.name_hi || card.name}</span>
                            <span className="text-text-muted text-xs">({card.name})</span>
                          </div>
                          <p className="text-text-muted">{card.meaning}</p>
                          {card.keywords && (
                            <div className="flex gap-2 mt-2">
                              {(Array.isArray(card.keywords) ? card.keywords : []).map((kw, j) => (
                                <span key={j} className={`text-xs px-2 py-0.5 rounded-full ${POSITIONS[i].border} ${POSITIONS[i].text} bg-white/5`}>{kw}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Combined Reading */}
                    <div className="glass-card p-6 mb-6 border border-gold/20" data-testid="combined-reading">
                      <h3 className="font-heading text-xl text-gold mb-3">
                        {lang === 'hi' ? 'संयुक्त व्याख्या' : 'Combined Reading'}
                      </h3>
                      <p className="text-text-muted text-lg leading-relaxed">{reading.combined_reading}</p>
                    </div>

                    {/* Guidance */}
                    <div className="glass-card p-5 mb-6 border border-purple-500/20">
                      <h3 className="font-heading text-lg text-purple-400 mb-2">
                        {lang === 'hi' ? 'आज का संदेश' : "Today's Message"}
                      </h3>
                      <p className="text-text-muted">{reading.guidance}</p>
                    </div>

                    {/* Upay */}
                    <div className="bg-gold/10 border border-gold/30 rounded-xl p-5 mb-8" data-testid="tarot-upay">
                      <div className="flex items-center gap-2 text-gold mb-2">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-heading text-lg">{lang === 'hi' ? 'वैदिक उपाय' : 'Vedic Remedy'}</span>
                      </div>
                      <p className="text-text-main font-medium text-lg">{reading.upay}</p>
                    </div>

                    {/* Energy + CTA */}
                    {reading.overall_energy && (
                      <p className="text-center text-gold/70 text-sm mb-6">
                        {lang === 'hi' ? 'आज की ऊर्जा' : "Today's Energy"}: <span className="text-gold font-bold text-lg">{reading.overall_energy}</span>
                      </p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button onClick={newReading} className="btn-gold-outline flex items-center justify-center gap-2" data-testid="new-reading-btn">
                        <RotateCcw className="w-4 h-4" /> {lang === 'hi' ? 'नई रीडिंग' : 'New Reading'}
                      </button>
                      <a href="/#contact" className="btn-gold flex items-center justify-center gap-2" data-testid="consult-tarot-cta">
                        {lang === 'hi' ? 'विस्तृत रीडिंग के लिए परामर्श लें' : 'Get Detailed Reading'}
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            <div className="text-center py-12 text-text-muted">
              {lang === 'hi' ? 'कुछ गलत हुआ, फिर try करें' : 'Something went wrong, please try again'}
              <br />
              <button onClick={newReading} className="btn-gold-outline mt-4">{lang === 'hi' ? 'फिर से करें' : 'Try Again'}</button>
            </div>
          )}
        </div>
      </section>

      {/* PRIYA KHURANA — Premium Personal Tarot Conversion */}
      <section className="py-16 bg-gradient-to-br from-bg-secondary to-bg-primary border-y border-gold/20" data-testid="tarot-priya-conversion">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Want a deeper, personal reading?</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">
              {lang === 'hi' ? 'व्यक्तिगत Tarot सत्र बुक करें — आचार्या Priya Khurana के साथ' : 'Book a Personal Tarot Session with Priya Khurana'}
            </h2>
            <p className="text-text-muted mt-3 max-w-2xl mx-auto text-sm">
              {lang === 'hi'
                ? 'दैनिक रीडिंग एक झलक है। गहरी clarity के लिए हमारी Dubai-based विशेषज्ञ Priya से confidential one-to-one consultation बुक करें।'
                : "Today's draw is a glimpse. For real clarity on a specific relationship, decision, or emotional pattern, book a confidential one-to-one session with our Dubai-based expert Priya."}
            </p>
          </div>

          <Link to="/experts/priya-khurana" className="group block rounded-3xl bg-bg-card border border-gold/30 hover:border-gold/60 overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(255,215,0,0.15)]" data-testid="tarot-priya-card">
            <div className="grid md:grid-cols-5">
              <div className="md:col-span-2 relative aspect-[4/3] md:aspect-auto md:min-h-[300px] overflow-hidden">
                <img
                  src="/experts/priya-khurana-card.png"
                  alt="Priya Khurana — Featured Tarot Expert at ViratAstro · Dubai"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-bg-primary/80 backdrop-blur-sm border border-gold/30 text-xs text-text-primary">
                  📍 Dubai, UAE · 6+ years
                </div>
              </div>
              <div className="md:col-span-3 p-7 sm:p-9 flex flex-col justify-center">
                <p className="text-xs tracking-[0.25em] text-gold uppercase mb-2">Featured Expert</p>
                <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-3 leading-tight">Priya Khurana</h3>
                <p className="text-sm text-text-muted leading-relaxed mb-4">
                  Tarot Teacher · Relationship Coach · Self-Awareness Mentor. Advanced training in <b className="text-text-primary">Canada</b> &amp; <b className="text-text-primary">Singapore</b>. Confidential online consultations worldwide.
                </p>
                <ul className="text-xs text-text-muted space-y-1 mb-5">
                  <li>✓ Love & Relationship Guidance</li>
                  <li>✓ Marriage & Compatibility Insights</li>
                  <li>✓ Emotional Awareness & Healing</li>
                  <li>✓ Self-Discovery & Personal Growth</li>
                </ul>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-bg-primary font-semibold text-sm group-hover:bg-yellow-400 transition">
                    Book a Session <ArrowRight className="w-4 h-4" />
                  </span>
                  <a
                    href="https://wa.me/971508238898?text=Namaste%20Priya%2C%20I%20would%20like%20to%20book%20a%20Tarot%20session."
                    target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition"
                  >
                    WhatsApp +971 50 823 8898
                  </a>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* SEO Schema */}
      <script type="application/ld+json" {...safeJsonLd({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "आज का टैरो कार्ड रीडिंग | Daily Tarot Card Reading",
        "description": "Free daily 3-card tarot spread reading with Vedic remedies. Past, Present, Future. आज का टैरो कार्ड रीडिंग।",
        "url": "https://viratastro.cloud/daily-tarot",
        "author": {"@type": "Person", "name": "Vivek Mudgal Astrologer"},
        "publisher": {"@type": "Organization", "name": "ViratAstro"}
      })} />

      <RelatedTools />
      <Footer />
    </div>
  );
}
